import { useQuery } from '@tanstack/react-query'
import axios from 'axios'

import { connectInternalApi, createAxiosInstance } from 'common/api'
import { sdkTagsKeys } from 'common/api/queryKeys/sdkTagsKeys'
import { env } from 'common/config/env'
import { useAppSelector } from 'common/hooks/redux'

// Raw instance: has auth headers but no global 404-redirect interceptor.
// Used for the tags GET so a 404 (endpoint not yet built) shows an empty
// state rather than hard-navigating to /404.
const sdkApi = createAxiosInstance(env.connectInternalEndPoint)

export interface SdkTag {
  _id: string
  tag_id: string
  name: string
  status: 'active' | 'revoked'
  created: string
  created_by?: string
  last_updated: string
  domains?: string[]
}

export const useGetSdkTagsQuery = () => {
  const accountId = useAppSelector((state) => state.authStates.accountId)
  return useQuery({
    queryKey: sdkTagsKeys.list(accountId),
    queryFn: async () => {
      try {
        const r = await sdkApi.get<SdkTag[]>(
          `v1/core/monorail/accounts/${accountId}/sdk-tags`
        )
        return r.data
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          return []
        }
        throw error
      }
    },
    enabled: !!accountId,
  })
}

export const createSdkTag = async ({
  accountId,
  name,
}: {
  accountId: string
  name: string
}): Promise<SdkTag[]> => {
  const result = await connectInternalApi.post<SdkTag[]>(
    `v1/core/monorail/accounts/${accountId}/sdk-tags`,
    { name }
  )
  return result.data
}

export const updateSdkTag = async ({
  accountId,
  id,
  name,
  domains,
}: {
  accountId: string
  id: string
  name?: string
  domains?: string[]
}): Promise<SdkTag[]> => {
  const result = await connectInternalApi.patch<SdkTag[]>(
    `v1/core/monorail/accounts/${accountId}/sdk-tags/${id}`,
    { name, domains }
  )
  return result.data
}

export const revokeSdkTag = async ({
  accountId,
  id,
}: {
  accountId: string
  id: string
}): Promise<SdkTag[]> => {
  const result = await connectInternalApi.delete<SdkTag[]>(
    `v1/core/monorail/accounts/${accountId}/sdk-tags/${id}`
  )
  return result.data
}

/**
 * A conversion event identified for the account by the reporting service, from the
 * daily-refreshed Snowflake SDK_CONVERSION_EVENTS table. Observation data only — the
 * blocked status is merged in client-side from the monorail blocklist.
 */
export interface SdkIdentifiedEvent {
  event: string
  first_seen?: string
  last_seen?: string
  count?: number
}

export const useGetSdkConversionEventsQuery = () => {
  const accountId = useAppSelector((state) => state.authStates.accountId)
  return useQuery({
    queryKey: sdkTagsKeys.conversionEvents(accountId),
    queryFn: async () => {
      try {
        const r = await sdkApi.get<SdkIdentifiedEvent[]>(
          `v1/reporting/account/${accountId}/sdk-conversion-events`
        )
        return r.data
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          return []
        }
        throw error
      }
    },
    enabled: !!accountId,
  })
}

export const useGetSdkBlockedEventsQuery = () => {
  const accountId = useAppSelector((state) => state.authStates.accountId)
  return useQuery({
    queryKey: sdkTagsKeys.blockedEvents(accountId),
    queryFn: async () => {
      try {
        const r = await sdkApi.get<string[]>(
          `v1/core/monorail/accounts/${accountId}/sdk-blocked-events`
        )
        return r.data
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          return []
        }
        throw error
      }
    },
    enabled: !!accountId,
  })
}

// Full replacement of the account's blocklist; returns the normalised list.
export const updateSdkBlockedEvents = async ({
  accountId,
  blockedEvents,
}: {
  accountId: string
  blockedEvents: string[]
}): Promise<string[]> => {
  const result = await connectInternalApi.put<string[]>(
    `v1/core/monorail/accounts/${accountId}/sdk-blocked-events`,
    { blocked_events: blockedEvents }
  )
  return result.data
}
